import { RefreshCw, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import type { Asset } from '../../../types/asset.types';
import { pickThumbnailUrl } from '../model/pickThumbnailUrl';
import {
  expiryLabel,
  isExpiringSoon,
  trashExpiresAtMs
} from './trashRetention';

type TrashCardProps = {
  asset: Asset;
  onRestore: (asset: Asset) => void;
  onPurge: (asset: Asset) => void;
  disabled?: boolean;
};

/**
 * One recoverable item in the Trash tab. Trash keeps every derivative intact
 * server-side, so the same 300w cache the grid uses is available here.
 */
const TrashCard = ({
  asset,
  onRestore,
  onPurge,
  disabled = false
}: TrashCardProps) => {
  const expiresAt = trashExpiresAtMs(asset);
  const soon = expiresAt !== null && isExpiringSoon(expiresAt);
  const thumbnailUrl = pickThumbnailUrl(asset.caches);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg ring-1 ring-border/40">
      {/* Same guard as DeleteConfirmDialog's thumb strip: an asset whose
          derivative caches the backend pruned arrives with no usable URL, and
          `<img src={undefined}>` paints the broken-image glyph. Falling back to
          the wrapper's own bg-muted block keeps the card's shape. */}
      <div className="relative aspect-[3/2] bg-muted">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt=""
            role="presentation"
            loading="lazy"
            className="h-full w-full object-cover opacity-80"
          />
        )}
      </div>
      <div className="flex flex-col gap-2 p-3">
        {expiresAt !== null && (
          <span
            className={cn(
              'text-xs font-semibold',
              soon ? 'text-warning' : 'text-muted-foreground'
            )}
          >
            {expiryLabel(expiresAt)}
          </span>
        )}
        <Typography
          variant="body-semibold"
          className="truncate text-foreground"
        >
          {asset.caption || 'Untitled Photo'}
        </Typography>
        <div className="mt-1 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            disabled={disabled}
            onClick={() => onRestore(asset)}
          >
            <RefreshCw className="h-4 w-4" />
            Restore
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            aria-label="Delete Permanently"
            disabled={disabled}
            onClick={() => onPurge(asset)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrashCard;
